// src/pages/SearchPage.tsx
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO, { breadcrumbLd } from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import ChipFilter from '../components/common/ChipFilter';
import SearchBar from '../components/common/SearchBar';
import ProgramCard from '../components/common/ProgramCard';
import Sidebar from '../components/common/Sidebar';
import InContentAd from '../components/ads/InContentAd';
import Icon from '../components/common/Icon';
import { searchPrograms, getPopularTags, supportPrograms } from '../data/supportPrograms';

const SearchPage: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const q = params.get('q') ?? '';
    const [term, setTerm] = useState(q);

    useEffect(() => setTerm(q), [q]);

    /* 입력을 디바운스해서 주소창에 반영 (뒤로가기 히스토리 오염 방지) */
    useEffect(() => {
        const id = window.setTimeout(() => {
            if (term === q) return;
            setParams(term ? { q: term } : {}, { replace: true });
        }, 300);
        return () => window.clearTimeout(id);
    }, [term, q, setParams]);

    const results = searchPrograms(q);
    const tags = getPopularTags(16);

    /* 카드 목록을 반으로 나누고 그 사이에 공통 광고를 한 번만 배치 */
    const cardsWithAd = (list: typeof supportPrograms) => {
        const half = Math.ceil(list.length / 2);
        return (
            <>
                <div className="gh-cards">
                    {list.slice(0, half).map(p => (
                        <ProgramCard key={p.id} program={p} />
                    ))}
                </div>
                <InContentAd />
                {half < list.length && (
                    <div className="gh-cards">
                        {list.slice(half).map(p => (
                            <ProgramCard key={p.id} program={p} />
                        ))}
                    </div>
                )}
            </>
        );
    };

    const crumbs = [
        { name: '홈', path: '/' },
        { name: '지원금 검색', path: '/search' },
    ];

    return (
        <>
            <SEO
                path="/search"
                title={q ? `'${q}' 검색 결과 - 정부지원금 통합 검색` : '정부지원금 통합 검색'}
                description={
                    q
                        ? `'${q}' 관련 정부지원금 검색 결과 ${results.length}건. 자격요건과 지원금액, 신청방법을 확인하세요.`
                        : `2026년 정부지원금 ${supportPrograms.length}개를 이름·키워드·지원대상으로 한 번에 검색하세요.`
                }
                keywords="정부지원금 검색, 지원금 조회, 나에게 맞는 지원금 찾기"
                noindex={Boolean(q)}
                jsonLd={breadcrumbLd(crumbs)}
            />

            <div className="gh-layout">
                <div>
                    <Breadcrumb items={crumbs} />

                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h1 className="gh-sec__title">
                                    <Icon name="search" size={20} />
                                    정부지원금 통합 검색
                                </h1>
                                <p className="gh-sec__desc">
                                    2026년 기준 {supportPrograms.length}개 제도에서 찾아드립니다
                                </p>
                            </div>
                        </div>

                        <SearchBar
                            searchTerm={term}
                            setSearchTerm={setTerm}
                            placeholder="지원금 이름, 키워드, 지원대상으로 검색 (예: 청년, 월세, 출산)"
                        />
                    </section>

                    <ChipFilter />

                    {q ? (
                        <section className="gh-sec">
                            <div className="gh-sec__head">
                                <div>
                                    <h2 className="gh-sec__title">'{q}' 검색 결과</h2>
                                </div>
                                <span className="gh-count">
                                    총 <b>{results.length}</b>건
                                </span>
                            </div>

                            {results.length === 0 ? (
                                <>
                                    <div className="gh-empty">
                                        <Icon name="search" size={40} />
                                        <p className="gh-empty__title">'{q}'에 대한 결과가 없습니다</p>
                                        <p className="gh-empty__desc">
                                            지원금 이름의 일부만 입력하거나, 아래 인기 키워드를 이용해 보세요.
                                        </p>
                                    </div>
                                    <InContentAd />
                                </>
                            ) : (
                                cardsWithAd(results)
                            )}
                        </section>
                    ) : (
                        <section className="gh-sec">
                            <div className="gh-sec__head">
                                <h2 className="gh-sec__title">전체 지원금</h2>
                                <span className="gh-count">
                                    총 <b>{supportPrograms.length}</b>건
                                </span>
                            </div>
                            {cardsWithAd(supportPrograms)}
                        </section>
                    )}

                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <h2 className="gh-sec__title">인기 검색 키워드</h2>
                        </div>
                        <div className="gh-tags">
                            {tags.map(t => (
                                <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="gh-tag">
                                    # {t}
                                </Link>
                            ))}
                        </div>
                    </section>

                </div>

                <Sidebar />
            </div>
        </>
    );
};

export default SearchPage;
